import { useMemo } from 'react'
import { createEntityHooks } from '../core/createEntityHooks'
import { useTasks } from './tasks'
import type { BaseDocument } from '../core/types'
/**
 * TaskRelation document interface
 */
export interface TaskRelationDocument extends BaseDocument {
  sourceTaskId: string
  sourceProjectId?: string | null
  targetTaskId: string
  targetProjectId?: string | null
  relationType: number
  sourceTaskName?: string
  sourceIdentifier?: string | null
  targetTaskName?: string
  targetIdentifier?: string | null
}

/**
 * Enriched TaskRelation with direction and fresh task names
 */
export interface EnrichedTaskRelation extends TaskRelationDocument {
  direction: 'outgoing' | 'incoming'
  linkedTaskId: string
  linkedTaskName: string
  linkedIdentifier: string | null
  linkedProjectId: string | null
}

/**
 * TaskRelation-specific hooks
 */
const hooks = createEntityHooks<TaskRelationDocument>('taskrelations')

/**
 * Get all relations involving a specific task, enriched with direction
 * and fresh task names from the local tasks collection.
 */
export const useTaskRelations = (taskId: string | undefined) => {
  const { data: asSource, isLoading: loadingSource } = hooks.useEntities(
    taskId ? { sourceTaskId: taskId, isDeleted: false } : undefined,
    { enabled: !!taskId },
  )
  const { data: asTarget, isLoading: loadingTarget } = hooks.useEntities(
    taskId ? { targetTaskId: taskId, isDeleted: false } : undefined,
    { enabled: !!taskId },
  )
  const { tasks } = useTasks({ isDeleted: false })

  const relations = useMemo<Array<EnrichedTaskRelation>>(() => {
    if (!taskId) return []

    const taskMap = new Map(tasks.map((t) => [t.id, t]))

    const outgoing = (asSource ?? []).map((r) => {
      const linkedTask = taskMap.get(r.targetTaskId)
      return {
        ...r,
        direction: 'outgoing' as const,
        linkedTaskId: r.targetTaskId,
        linkedTaskName: linkedTask?.name ?? r.targetTaskName ?? '',
        linkedIdentifier: (linkedTask as any)?.identifier ?? r.targetIdentifier ?? null,
        linkedProjectId: linkedTask?.projectId ?? r.targetProjectId ?? null,
      }
    })

    const incoming = (asTarget ?? []).map((r) => {
      const linkedTask = taskMap.get(r.sourceTaskId)
      return {
        ...r,
        direction: 'incoming' as const,
        linkedTaskId: r.sourceTaskId,
        linkedTaskName: linkedTask?.name ?? r.sourceTaskName ?? '',
        linkedIdentifier: (linkedTask as any)?.identifier ?? r.sourceIdentifier ?? null,
        linkedProjectId: linkedTask?.projectId ?? r.sourceProjectId ?? null,
      }
    })

    // Filter out relations where the linked task is deleted
    return [...outgoing, ...incoming].filter((r) => {
      const linkedTask = taskMap.get(r.linkedTaskId)
      return !linkedTask || !linkedTask.isDeleted
    })
  }, [taskId, asSource, asTarget, tasks])

  return { relations, isLoading: loadingSource || loadingTarget }
}

export const useTaskRelation = (id: string | undefined) => {
  const { data, isLoading } = hooks.useEntity(id)
  return { taskRelation: data, isLoading }
}

/**
 * Get all active relations within a workspace. Used for cycle detection across
 * the full directional graph, not just the relations attached to one task.
 */
export const useAllWorkspaceTaskRelations = (
  workspaceId: string | null | undefined,
) => {
  const { data, isLoading } = hooks.useEntities(
    workspaceId ? { envId: workspaceId, isDeleted: false } : undefined,
    { enabled: !!workspaceId },
  )
  return { relations: data ?? [], isLoading }
}

export const useTaskRelationMutations = () => {
  const { create, update, delete: deleteEntity } = hooks.useMutations()
  return {
    createTaskRelation: create,
    updateTaskRelation: update,
    deleteTaskRelation: deleteEntity,
  }
}
